import { clamp } from './clamp';
import { MARKER_GAP, MARKER_HEIGHT, MARKER_WIDTH } from './constants';
import { isCompactViewport } from './policy';
import { normalizeSectionId } from './section-ids';
import type {
  ArenaPriceMarker,
  MarkerDensity,
  MarkerViewport,
  Point,
  SectionMeasurement,
  SelectPriceMarkersOptions,
} from './types';

export function selectPriceMarkers({
  sections,
  policy,
  selectedSectionIds,
  focusedSectionId,
  hiddenSectionId,
  viewport,
}: SelectPriceMarkersOptions): ArenaPriceMarker[] {
  const selectedIds = new Set(
    selectedSectionIds.map((sectionId) => normalizeSectionId(sectionId))
  );
  const focusedId = focusedSectionId
    ? normalizeSectionId(focusedSectionId)
    : undefined;
  const hiddenId = hiddenSectionId
    ? normalizeSectionId(hiddenSectionId)
    : undefined;
  const visibleSections = sections
    .filter(
      (section) =>
        section.sectionId !== hiddenId &&
        isMarkerAnchorVisible(section, viewport)
    )
    .map((section) => fitMarkerAnchor(section, viewport));

  if (!visibleSections.length) return [];

  const focusPoint = getFocusPoint({
    sections: visibleSections,
    selectedIds,
    focusedId,
    viewport,
  });

  const orderedSections = [...visibleSections].sort((left, right) => {
    const leftPriority = getSelectionPriority({
      sectionId: left.sectionId,
      selectedIds,
      focusedId,
    });
    const rightPriority = getSelectionPriority({
      sectionId: right.sectionId,
      selectedIds,
      focusedId,
    });

    return (
      leftPriority - rightPriority ||
      left.price - right.price ||
      distanceToPoint(left, focusPoint) - distanceToPoint(right, focusPoint) ||
      left.sectionName.localeCompare(right.sectionName)
    );
  });

  const selectedMarkers: SectionMeasurement[] = [];
  const regularMarkers: SectionMeasurement[] = [];
  const maxMarkers = policy.maxMarkers ?? Number.POSITIVE_INFINITY;

  orderedSections.forEach((section) => {
    if (selectedIds.has(section.sectionId) || section.sectionId === focusedId) {
      selectedMarkers.push(section);
    } else {
      regularMarkers.push(section);
    }
  });

  const chosenSections: SectionMeasurement[] = [];
  const orderedRegularMarkers = isCompactViewport(viewport)
    ? regularMarkers
    : orderSectionsForCoverage(regularMarkers, policy.kind, viewport);

  for (const section of [...selectedMarkers, ...orderedRegularMarkers]) {
    if (chosenSections.length >= maxMarkers) break;
    if (chosenSections.some((chosen) => markersOverlap(chosen, section))) {
      continue;
    }

    chosenSections.push(section);
  }

  return chosenSections.map((section) => ({
    sectionId: section.sectionId,
    sectionName: section.sectionName,
    price: section.price,
    availableQuantity: section.availableQuantity,
    isFeatured: section.isFeatured,
    left: section.centerX,
    top: section.centerY,
    density: policy.kind,
    isSelected:
      selectedIds.has(section.sectionId) || section.sectionId === focusedId,
    isVisible: true,
  }));
}

function fitMarkerAnchor(
  section: SectionMeasurement,
  viewport: MarkerViewport
): SectionMeasurement {
  return {
    ...section,
    centerX: clamp(
      section.centerX,
      MARKER_WIDTH / 2,
      viewport.width - MARKER_WIDTH / 2
    ),
    centerY: clamp(
      section.centerY,
      MARKER_HEIGHT + MARKER_GAP,
      viewport.height - MARKER_GAP
    ),
  };
}

function isMarkerAnchorVisible(
  section: SectionMeasurement,
  viewport: MarkerViewport
): boolean {
  return (
    section.visible &&
    section.centerX >= 0 &&
    section.centerX <= viewport.width &&
    section.centerY >= 0 &&
    section.centerY <= viewport.height
  );
}

export function positionPriceMarkers({
  markers,
  sections,
  viewport,
  hiddenSectionId,
}: {
  markers: readonly ArenaPriceMarker[];
  sections: readonly SectionMeasurement[];
  viewport: MarkerViewport;
  hiddenSectionId?: string;
}): ArenaPriceMarker[] {
  const measurementsById = new Map(
    sections.map((section) => [section.sectionId, section])
  );
  const hiddenId = hiddenSectionId
    ? normalizeSectionId(hiddenSectionId)
    : undefined;

  const visibleSections: SectionMeasurement[] = [];
  return markers.map((marker) => {
    const measurement = measurementsById.get(marker.sectionId);
    const section = measurement && fitMarkerAnchor(measurement, viewport);
    const isVisible = Boolean(
      measurement &&
      measurement.sectionId !== hiddenId &&
      isMarkerAnchorVisible(measurement, viewport) &&
      section &&
      !visibleSections.some((chosen) => markersOverlap(chosen, section))
    );
    if (section && isVisible) visibleSections.push(section);

    return section && isVisible
      ? {
          ...marker,
          left: section.centerX,
          top: section.centerY,
          isVisible: true,
        }
      : { ...marker, isVisible: false };
  });
}

function interleave<T>(bands: T[][]): T[] {
  const orderedSections: T[] = [];
  const longestBand = Math.max(...bands.map((band) => band.length));

  for (let round = 0; round < longestBand; round += 1) {
    bands.forEach((band) => {
      const section = band[round];
      if (section) orderedSections.push(section);
    });
  }

  return orderedSections;
}

function orderSectionsForCoverage(
  sections: readonly SectionMeasurement[],
  density: MarkerDensity,
  viewport: MarkerViewport
): SectionMeasurement[] {
  if (sections.length < 2) return [...sections];

  const columns = density === 'detail' ? 6 : density === 'regional' ? 5 : 4;
  const rows = density === 'detail' ? 5 : density === 'regional' ? 4 : 3;
  const radialBands = new Map<number, SectionMeasurement[]>();

  sections.forEach((section) => {
    const band = getRadialBand(section, viewport, density);
    const radialBand = radialBands.get(band) ?? [];

    radialBand.push(section);
    radialBands.set(band, radialBand);
  });

  const sortedBands = [...radialBands.values()]
    .map((band) => orderSectionsByGrid(band, viewport, columns, rows))
    .sort((left, right) => left[0].price - right[0].price);

  return interleave(sortedBands);
}

function orderSectionsByGrid(
  sections: readonly SectionMeasurement[],
  viewport: MarkerViewport,
  columns: number,
  rows: number
): SectionMeasurement[] {
  const buckets = new Map<string, SectionMeasurement[]>();

  sections.forEach((section) => {
    const column = getGridCoordinate(section.centerX, viewport.width, columns);
    const row = getGridCoordinate(section.centerY, viewport.height, rows);
    const bucketId = `${column}:${row}`;
    const bucket = buckets.get(bucketId) ?? [];

    bucket.push(section);
    buckets.set(bucketId, bucket);
  });

  const sortedBuckets = [...buckets.values()].sort(
    (left, right) => left[0].price - right[0].price
  );

  return interleave(sortedBuckets);
}

function getRadialBand(
  section: SectionMeasurement,
  viewport: MarkerViewport,
  density: MarkerDensity
): number {
  const centerX = viewport.width / 2;
  const centerY = viewport.height / 2;
  const normalizedX = (section.centerX - centerX) / Math.max(centerX, 1);
  const normalizedY = (section.centerY - centerY) / Math.max(centerY, 1);
  const normalizedDistance = Math.hypot(normalizedX, normalizedY);
  const bandCount = density === 'detail' ? 4 : 3;

  return clamp(
    Math.floor((normalizedDistance / 1.15) * bandCount),
    0,
    bandCount - 1
  );
}

function getGridCoordinate(value: number, size: number, count: number): number {
  if (!Number.isFinite(value) || !Number.isFinite(size) || size <= 0) {
    return 0;
  }

  return clamp(Math.floor((value / size) * count), 0, count - 1);
}

function getSelectionPriority({
  sectionId,
  selectedIds,
  focusedId,
}: {
  sectionId: string;
  selectedIds: ReadonlySet<string>;
  focusedId?: string;
}): number {
  if (sectionId === focusedId) return 0;
  if (selectedIds.has(sectionId)) return 1;
  return 2;
}

function getFocusPoint({
  sections,
  selectedIds,
  focusedId,
  viewport,
}: {
  sections: readonly SectionMeasurement[];
  selectedIds: ReadonlySet<string>;
  focusedId?: string;
  viewport: MarkerViewport;
}): Point {
  const focusedSection =
    sections.find(({ sectionId }) => sectionId === focusedId) ??
    sections.find(({ sectionId }) => selectedIds.has(sectionId));

  return focusedSection
    ? { x: focusedSection.centerX, y: focusedSection.centerY }
    : { x: viewport.width / 2, y: viewport.height / 2 };
}

function distanceToPoint(section: SectionMeasurement, point: Point): number {
  return Math.hypot(section.centerX - point.x, section.centerY - point.y);
}

function markersOverlap(
  first: SectionMeasurement,
  second: SectionMeasurement
): boolean {
  return (
    Math.abs(first.centerX - second.centerX) < MARKER_WIDTH + MARKER_GAP &&
    Math.abs(first.centerY - second.centerY) < MARKER_HEIGHT + MARKER_GAP
  );
}
